/**
 * Helpers for the communities list table.
 *
 * Background: communities onboarded after the forward catalog-mirror was
 * suspended (FLEXO-6033) have `communityCode === null` — only
 * `communityExternalId` is set. The list previously showed the code and sorted
 * by it, so those communities rendered with a blank identifier and floated to
 * the top of the default (code-based) sort. The external id is always
 * populated and equals `communityCode` whenever both exist, so it is the
 * reliable identifier for both display and sorting.
 *
 * The external id arrives under two names depending on the catalog endpoint:
 * - `communityExternalId` from GET /catalog/v1_4/entities (EntityCommunity);
 * - `externalId` from GET /catalog/v1_3/organization/{id}/communities
 *   (CommunityListItem), which is what `CommunitiesResolver` stores in
 *   `metadata.communities` and the communities list renders (CM-5134).
 */

export interface CommunityLike {
  communityCode?: string | null;
  communityExternalId?: string | null;
  externalId?: string | null;
}

/**
 * The identifier shown for a community in the list. Prefers the external id
 * under either of its wire names (always present, and identical to the code
 * when both exist), falling back to the legacy code, then to an empty string
 * so the label never renders `undefined`.
 */
export function getCommunityDisplayId(community: CommunityLike | null | undefined): string {
  return (
    community?.communityExternalId ||
    community?.externalId ||
    community?.communityCode ||
    ''
  );
}

/**
 * Default ascending (A–Z) comparator for the communities list, keyed on the
 * displayed identifier (external id). Locale-aware and stable — returns 0 for
 * equal ids instead of the legacy `> ? 1 : -1` which never returned 0.
 */
export function compareCommunitiesByDisplayId(
  a: CommunityLike | null | undefined,
  b: CommunityLike | null | undefined
): number {
  return getCommunityDisplayId(a).localeCompare(getCommunityDisplayId(b));
}
